/**
 * Asset Management · Maintenance & PM plans.
 *
 * What preventive work is overdue and due, whether it is done on time, how
 * much of the work is planned rather than breakdowns, and which assets break
 * most (MTBF and MTTR from the corrective work on each). Plans are made here
 * one asset at a time or for every asset of a type; the worker raises their
 * work orders as drafts when due, and a planner can raise what is due now.
 *
 * The coverage line matters here too: MTBF needs work orders that name their
 * asset, and on the real estate most historic work orders do not.
 */
import { Fragment, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { hasPermission, PERMISSION } from "../../api/dataSource";
import { useAmApplyPlan, useAmEditPlan, useAmGeneratePlans, useAmMaintenance, useAmRegister } from "../../api/queries";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { PlanDatesForm } from "../entryForms/PlanDatesForm";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Segmented,
  Select,
  Skeleton,
  Table,
} from "../../design/components";
import { PLAN_STATE_LABEL, type AmMaintenance, type AmPlan } from "../../domain/assetManagement";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { ColumnChart, Coverage, Criticality, day, errorText, KIND_LABEL, money, pct, s, SUITE, Tiles } from "./shared";

type Tab = "plans" | "reliability" | "mix";
const TABS: { value: Tab; label: string }[] = [
  { value: "plans", label: "Plans" },
  { value: "reliability", label: "Failures by asset" },
  { value: "mix", label: "Planned and unplanned work" },
];

const STATE_ORDER: Record<AmPlan["state"], number> = { overdue: 0, due: 1, scheduled: 2, waiting: 3, paused: 4 };
const STATE_TONE: Record<AmPlan["state"], string> = {
  overdue: "var(--nogo)",
  due: "var(--caution)",
  scheduled: "var(--go)",
  waiting: "var(--dim)",
  paused: "var(--dim)",
};

export function AssetMaintenancePage() {
  const session = useSession();
  const q = useAmMaintenance();
  const generate = useAmGeneratePlans();
  const [tab, setTab] = useState<Tab>("plans");
  const m = q.data;
  const canPlan = hasPermission(session.principal, PERMISSION.assetWrite);
  const canRaise = hasPermission(session.principal, PERMISSION.workOrdersWrite);

  useScreenContext(
    m
      ? {
          route: "/asset-maintenance",
          title: "Maintenance plans",
          summary: [
            t("{n} active plans: {o} overdue, {d} due within their lead time, {m} due in the next 30 days.", {
              n: m.summary.active,
              o: m.summary.overdue,
              d: m.summary.due,
              m: m.summary.due30Days,
            }),
            m.summary.compliancePct != null
              ? t("PM compliance over 12 months is {p}%.", { p: m.summary.compliancePct })
              : t("No preventive work has come due in the last 12 months."),
          ],
          offers: [
            { label: t("What maintenance is overdue?"), ask: "what preventive maintenance is overdue?" },
            { label: t("Which assets fail most?"), ask: "which assets fail most often?" },
          ],
          worksInFull: true,
        }
      : null,
  );

  return (
    <Page
      eyebrow={t("{site} · {suite}", { site: session.site.code, suite: t(SUITE) })}
      title="Maintenance plans"
      actions={
        canRaise ? (
          <Button onClick={() => generate.mutate()} disabled={generate.isPending} data-testid="am-generate">
            {generate.isPending ? t("Creating…") : t("Create due work orders now")}
          </Button>
        ) : undefined
      }
    >
      {generate.data && (
        <p className={s.ok} data-testid="am-generated">
          {generate.data.created.length
            ? t("{n} work orders raised as drafts: {list}", {
                n: generate.data.created.length,
                list: generate.data.created
                  .map((c) => `${c.workOrder} (${c.asset})`)
                  .slice(0, 10)
                  .join(", "),
              })
            : t("Nothing was due that did not already have an open work order.")}
        </p>
      )}
      {generate.error && <p className={s.err}>{errorText(generate.error)}</p>}
      {q.isLoading && <Skeleton rows={8} />}
      {q.error && <ErrorState error={q.error} retry={() => void q.refetch()} />}
      {m && (
        <>
          <Tiles
            items={[
              { key: "active", label: t("Active plans"), value: m.summary.active },
              {
                key: "overdue",
                label: t("Overdue"),
                value: m.summary.overdue,
                tone: m.summary.overdue ? "bad" : "good",
              },
              {
                key: "due30",
                label: t("Due in 30 days"),
                value: m.summary.due30Days,
                tone: m.summary.due30Days ? "warn" : undefined,
              },
              {
                key: "compliance",
                label: t("PM compliance, 12 months"),
                value: pct(m.summary.compliancePct),
                sub: t("{a} of {b} done by their due date", { a: m.summary.pmOnTime12m, b: m.summary.pmCompleted12m }),
                tone:
                  m.summary.compliancePct == null
                    ? undefined
                    : m.summary.compliancePct >= 90
                      ? "good"
                      : m.summary.compliancePct >= 75
                        ? "warn"
                        : "bad",
              },
              {
                key: "planned",
                label: t("Planned share of work"),
                value: pct(m.summary.plannedSharePct),
                sub: t("preventive, predictive and inspection ÷ all work"),
              },
            ]}
          />
          <Coverage
            lines={[
              {
                label: t("work orders in 24 months name their asset"),
                of: m.coverage.workOrders24m,
                n: m.coverage.linkedToAsset,
              },
            ]}
          />
          <Segmented value={tab} options={TABS} onChange={setTab} ariaLabel="Maintenance views" />
          {tab === "plans" && <PlansTab m={m} canPlan={canPlan} />}
          {tab === "reliability" && <Reliability m={m} />}
          {tab === "mix" && <Mix m={m} />}
          <p className={s.note}>
            {t("How it is worked out:")} {m.method}
          </p>
        </>
      )}
    </Page>
  );
}

function PlansTab({ m, canPlan }: { m: AmMaintenance; canPlan: boolean }) {
  const edit = useAmEditPlan();
  const [show, setShow] = useState<"open" | "all">("open");
  /* build 6B: the plan whose interval and due date are being changed */
  const [editing, setEditing] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const plans = useMemo(
    () =>
      [...m.plans]
        .filter((p) => show === "all" || p.state === "overdue" || p.state === "due" || p.state === "scheduled")
        .sort(
          (a, b) => STATE_ORDER[a.state] - STATE_ORDER[b.state] || (a.nextDueAt ?? "").localeCompare(b.nextDueAt ?? ""),
        ),
    [m.plans, show],
  );
  return (
    <>
      <Card
        title={t("Plans")}
        sub={t("Overdue first, then by due date.")}
        actions={
          <Segmented
            value={show}
            options={[
              { value: "open", label: "Active" },
              { value: "all", label: "All" },
            ]}
            onChange={setShow}
            ariaLabel="Which plans"
          />
        }
      >
        {saved && (
          <p className={s.note} role="status">
            {saved}
          </p>
        )}
        {plans.length ? (
          <Table>
            <thead>
              <tr>
                <th>{t("Asset")}</th>
                <th>{t("Plan")}</th>
                <th>{t("Every")}</th>
                <th>{t("Last done")}</th>
                <th>{t("Next due")}</th>
                <th>{t("State")}</th>
                <th>{t("Work order")}</th>
                {canPlan && <th />}
              </tr>
            </thead>
            <tbody>
              {plans.slice(0, 300).map((p) => (
                <Fragment key={p.id}>
                <tr>
                  <td className={s.mono}>
                    {p.assetId ? <Link to={`/asset-register/${encodeURIComponent(p.assetId)}`}>{p.tag}</Link> : p.tag}
                  </td>
                  <td>{p.title}</td>
                  <td>
                    {p.trigger === "calendar"
                      ? t("{n} days", { n: p.intervalDays ?? "" })
                      : `${p.intervalUsage ?? ""} ${p.usageMeasurement ?? ""}`}
                  </td>
                  <td className={s.mono}>{day(p.lastDoneAt)}</td>
                  <td className={s.mono}>{day(p.nextDueAt)}</td>
                  <td>
                    <span className={s.chip} style={{ ["--chip" as string]: STATE_TONE[p.state] }}>
                      {t(PLAN_STATE_LABEL[p.state])}
                    </span>
                  </td>
                  <td className={s.mono}>{p.openWorkOrder ?? "—"}</td>
                  {canPlan && (
                    <td>
                      <button
                        type="button"
                        className={s.linkBtn}
                        disabled={edit.isPending}
                        onClick={() => edit.mutate({ id: p.id, patch: { active: !p.active } })}
                      >
                        {p.active ? t("Pause") : t("Resume")}
                      </button>{" "}
                      <button
                        type="button"
                        className={s.linkBtn}
                        onClick={() => setEditing(editing === p.id ? null : p.id)}
                        data-testid={`plan-edit-${p.id}`}
                      >
                        {t("Change dates")}
                      </button>
                    </td>
                  )}
                </tr>
                {editing === p.id && (
                  <tr>
                    <td colSpan={8}>
                      <PlanDatesForm
                        plan={p}
                        onDone={(msg) => {
                          setEditing(null);
                          if (msg) setSaved(msg);
                        }}
                      />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </Table>
        ) : (
          <EmptyState>{t("No maintenance plan yet. Add one below, or on an asset's own page.")}</EmptyState>
        )}
      </Card>
      {canPlan && <ApplyPlan />}
    </>
  );
}

function ApplyPlan() {
  const reg = useAmRegister({});
  const apply = useAmApplyPlan();
  const [v, setV] = useState({ type: "", title: "", interval: "90", first: "", lead: "14" });
  const types = reg.data?.types ?? [];
  const chosen = types.find((ty) => ty.key === v.type);
  return (
    <Card
      title={t("Plan a whole asset type")}
      sub={t("Add one plan to every asset of a type that lacks it.")}
    >
      <div className={s.form}>
        <Field label="Asset type">
          <Select value={v.type} onChange={(e) => setV({ ...v, type: e.target.value })} data-testid="am-apply-type">
            <option value="">{t("Choose a type")}</option>
            {types.map((ty) => (
              <option key={ty.key} value={ty.key}>
                {ty.name} ({ty.assets})
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Title">
          <Input
            value={v.title}
            onChange={(e) => setV({ ...v, title: e.target.value })}
            placeholder={t("e.g. Quarterly service")}
            data-testid="am-apply-title"
          />
        </Field>
        <Field label="Every (days)">
          <Input inputMode="numeric" value={v.interval} onChange={(e) => setV({ ...v, interval: e.target.value })} />
        </Field>
        <Field label="First due">
          <Input type="date" value={v.first} onChange={(e) => setV({ ...v, first: e.target.value })} />
        </Field>
        <Field label="Raise this many days ahead">
          <Input inputMode="numeric" value={v.lead} onChange={(e) => setV({ ...v, lead: e.target.value })} />
        </Field>
      </div>
      <div className={s.formFoot}>
        <Button
          variant="primary"
          disabled={!v.type || v.title.trim().length < 3 || !Number(v.interval) || apply.isPending}
          onClick={() =>
            apply.mutate({
              assetType: v.type,
              plan: {
                title: v.title.trim(),
                trigger: "calendar",
                intervalDays: Number(v.interval),
                firstDueOn: v.first || undefined,
                leadDays: Number(v.lead) || 0,
              },
            })
          }
          data-testid="am-apply-go"
        >
          {chosen ? t("Plan {n} assets", { n: chosen.assets }) : t("Plan the type")}
        </Button>
        {apply.data && <span className={s.ok}>{t("{n} plans created.", { n: apply.data.created })}</span>}
        {apply.error && <span className={s.err}>{errorText(apply.error)}</span>}
      </div>
    </Card>
  );
}

function Reliability({ m }: { m: AmMaintenance }) {
  return (
    <Card
      title={t("Assets that fail most")}
      sub={t("Repairs in 24 months, with failure and repair times")}
    >
      {m.badActors.length ? (
        <Table>
          <thead>
            <tr>
              <th>{t("Tag")}</th>
              <th>{t("Name")}</th>
              <th>{t("Criticality")}</th>
              <th className={s.num}>{t("Failures")}</th>
              <th className={s.num}>{t("MTBF (days)")}</th>
              <th className={s.num}>{t("MTTR (hours)")}</th>
              <th className={s.num}>{t("Cost")}</th>
            </tr>
          </thead>
          <tbody>
            {m.badActors.map((b) => (
              <tr key={b.id}>
                <td className={s.mono}>
                  <Link to={`/asset-register/${encodeURIComponent(b.id)}`}>{b.tag}</Link>
                </td>
                <td>{b.name}</td>
                <td>
                  <Criticality value={b.criticality} />
                </td>
                <td className={s.num}>{b.failures}</td>
                <td className={s.num}>{b.mtbfDays ?? "—"}</td>
                <td className={s.num}>{b.mttrHours ?? "—"}</td>
                <td className={s.num}>{money(b.cost)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <EmptyState>
          {t("No repair work order names an asset yet, so failure rates can't be shown.")}
        </EmptyState>
      )}
    </Card>
  );
}

function Mix({ m }: { m: AmMaintenance }) {
  const kinds = Object.entries(m.workMix12m).sort((a, b) => b[1].count - a[1].count);
  if (!kinds.length) return <EmptyState>{t("No work orders in the last 12 months.")}</EmptyState>;
  return (
    <div className={s.split}>
      <Card title={t("Work orders by kind, 12 months")}>
        <ColumnChart
          label={t("Work orders by kind, 12 months")}
          format={(v) => String(v)}
          data={kinds.map(([k, v]) => ({ key: k, label: t(KIND_LABEL[k] ?? k), value: v.count }))}
        />
      </Card>
      <Card title={t("Cost by kind, 12 months")}>
        <ColumnChart
          label={t("Cost by kind, 12 months")}
          format={(v) => money(v)}
          data={kinds.map(([k, v]) => ({ key: k, label: t(KIND_LABEL[k] ?? k), value: v.cost }))}
        />
      </Card>
    </div>
  );
}
